import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Link,
  Text,
  useToast,
} from "@chakra-ui/react";
import { Header } from "../../components/Header";
import { Footer } from "../../components/Footer";
import { Limites } from "../Pdv/LImites";
import { Navigate, useParams } from "react-router-dom";
import { usePedido } from "../../hooks/pagamento/usePedido";
import { Loading } from "../../components/Loading";
import { useMutationLinkPagamento } from "../../hooks/pagamento/mutationLinkPagamento";
import { useCallback, useEffect, useState } from "react";
import { CheckoutProgress, PagamentoLink, PedidoStatusCode } from "../../utils/interfaces";
import Swal from "sweetalert2";
import { formatBRL } from "../../utils/currency";
import { consultarLinkPagamento } from "../../hooks/pagamento/useConsultarLinkPagamento";
import { BiCopy, BiLinkExternal } from "react-icons/bi";
import { AxiosErrorResponse } from "../../hooks/pdv/mutationReserva";
import { api } from "../../libs/axios";
import { useRef } from "react";
import { useCheckoutStatus } from "../../hooks/pagamento/useCheckoutStatus";
import { CheckoutProgressPanel } from "../../components/CheckoutProgress";
import { createCheckoutProgressOverride, mergeCheckoutProgress } from "../../utils/checkoutProgress";
import { useVisualCheckoutProgress } from "../../hooks/pagamento/useVisualCheckoutProgress";

export function PagamentoLinkWebpag() {
  const { id } = useParams() as { id: string };
  const {
    data,
    isLoading,
    isFetching,
    isError,
    isRestoringPedido,
    isPedidoPageVisible,
    pedidoValidationFailed,
    retryPedidoValidation,
  } = usePedido({ id, revalidateOnHistoryRestore: true });
  const pedido = data?.pedido;
  const hasValidPedido = pedido !== undefined && pedido !== null;
  const pedidoDisponivelParaPagamento = hasValidPedido && [
    PedidoStatusCode.Aberto,
    PedidoStatusCode.Reservado,
  ].includes(pedido.status);
  const mutation = useMutationLinkPagamento(id);
  const [pagamentoLink, setPagamentoLink] = useState<PagamentoLink | null>(null);
  const [localProgress, setLocalProgress] = useState<ReturnType<typeof createCheckoutProgressOverride> | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [isAutoCancelling, setIsAutoCancelling] = useState(false);
  const isOpen = true;
  const toast = useToast();
  const cancelOnExpireTriggered = useRef(false);
  const errorHandledRef = useRef(false);
  const completionHandledRef = useRef(false);
  const checkoutEffectsAllowed =
    !isLoading &&
    !isFetching &&
    !isError &&
    !isRestoringPedido &&
    isPedidoPageVisible &&
    !pedidoValidationFailed &&
    pedidoDisponivelParaPagamento;
  const { data: checkoutProgress, refetch } = useCheckoutStatus(id, pedidoDisponivelParaPagamento && checkoutEffectsAllowed);

  const parseBrDate = (value?: string): Date | null => {
    if (!value) return null;
    const match = value.match(
      /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/
    );
    if (!match) return null;
    const [, dd, mm, yyyy, hh, mi, ss] = match;
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(mi),
      Number(ss)
    );
  };

  const parseIsoLikeDate = (value?: string): Date | null => {
    if (!value) return null;
    const normalized = value.includes("T") ? value : value.replace(" ", "T");
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const getPedidoDeadline = (): Date | null => {
    const allBilhetes = Object.values(data?.bilhetes ?? {})
      .flatMap((agrupamentos) => Array.isArray(agrupamentos) ? agrupamentos : [])
      .flatMap((agrupamento) => Array.isArray(agrupamento.viagens) ? agrupamento.viagens : [])
      .flatMap((viagem) => Array.isArray(viagem.bilhetes) ? viagem.bilhetes : []);

    const dates = allBilhetes
      .map((bilhete) => parseBrDate(bilhete.expires_at))
      .filter((date): date is Date => date !== null);

    if (dates.length === 0) return null;

    return new Date(Math.min(...dates.map((date) => date.getTime())));
  };

  useEffect(() => {
    const carregarLink = async () => {
      try {
        setLocalProgress(
          createCheckoutProgressOverride(
            id,
            "link",
            "generating_link",
            "Gerando link de pagamento",
            "Estamos preparando o link para que o pagamento possa ser realizado.",
          ),
        );
        const link = await mutation.mutateAsync();
        setPagamentoLink(link);
        setLocalProgress(
          createCheckoutProgressOverride(
            id,
            "link",
            "waiting_payment",
            "Aguardando pagamento",
            "Link gerado com sucesso. Agora estamos aguardando a confirmação do pagamento.",
          ),
        );
      } catch (error) {
        setLocalProgress(null);
        const err = error as AxiosErrorResponse;
        const message =
          err.response?.data?.data?.message ??
          err.response?.data?.message ??
          "Erro ao gerar link de pagamento";
        const redirectTo = err.response?.data?.data?.redirect_to;

        if (redirectTo) {
          sessionStorage.setItem('gopass_redirect_error_message', message);
          window.location.href = redirectTo;
          return;
        }

        const isWarningMessage = message
          .toLowerCase()
          .includes("não está disponível para gerar link de pagamento");

        Swal.fire({
          icon: isWarningMessage ? "warning" : "error",
          title: isWarningMessage ? "Aviso" : "Erro",
          text: message,
        });
      }
    };

    if (isOpen && pedidoDisponivelParaPagamento && checkoutEffectsAllowed) {
      carregarLink();
    }
  }, [isOpen, pedidoDisponivelParaPagamento, checkoutEffectsAllowed]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    const verificarPagamento = async () => {
      try {
        await consultarLinkPagamento(id);
        setLocalProgress(
          createCheckoutProgressOverride(
            id,
            "link",
            "payment_registered",
            "Pagamento registrado",
            "Pagamento confirmado com sucesso. Agora vamos confirmar o pedido.",
          ),
        );
        clearInterval(interval);
        await refetch();
      } catch (error) {
        const err = error as AxiosErrorResponse;
        if (err.response?.status === 422) {
          const message =
            err.response?.data?.data?.message ??
            err.response?.data?.message ??
            "Erro ao consultar pagamento";
          const redirectTo = err.response?.data?.data?.redirect_to;

          if (redirectTo) {
            clearInterval(interval);
            sessionStorage.setItem('gopass_redirect_error_message', message);
            window.location.href = redirectTo;
            return;
          }

          Swal.fire({
            icon: "error",
            title: "Erro no pagamento",
            text: message,
          });
          clearInterval(interval);
        }
      }
    };

    if (pagamentoLink && pedidoDisponivelParaPagamento && checkoutEffectsAllowed) {
      interval = setInterval(verificarPagamento, 10000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [id, pagamentoLink, pedidoDisponivelParaPagamento, checkoutEffectsAllowed]);

  useEffect(() => {
    if (!checkoutProgress) {
      return;
    }

    if (checkoutProgress.status === "failed" && !errorHandledRef.current) {
      errorHandledRef.current = true;
      completionHandledRef.current = true;
      setLocalProgress(null);
      const message = checkoutProgress.error_message || checkoutProgress.description || "Erro no pagamento";

      if (checkoutProgress.redirect_to) {
        sessionStorage.setItem('gopass_redirect_error_message', message);
        window.location.href = checkoutProgress.redirect_to;
        return;
      }

      Swal.fire({
        icon: "error",
        title: "Erro no pagamento",
        text: message,
      });
    }
  }, [checkoutProgress, id]);

  useEffect(() => {
    if (checkoutProgress?.step && checkoutProgress.step !== "waiting_payment") {
      setLocalProgress(null);
    }
  }, [checkoutProgress?.step]);

  useEffect(() => {
    if (!pagamentoLink) {
      setSecondsLeft(null);
      return;
    }

    const linkDeadline = parseIsoLikeDate(pagamentoLink.expira_em);
    const pedidoDeadline = getPedidoDeadline();

    const deadlineCandidates = [linkDeadline, pedidoDeadline].filter(
      (date): date is Date => date !== null
    );

    if (deadlineCandidates.length === 0) {
      setSecondsLeft(null);
      return;
    }

    const deadline = new Date(
      Math.min(...deadlineCandidates.map((date) => date.getTime()))
    );

    const updateCounter = () => {
      const diff = Math.ceil((deadline.getTime() - Date.now()) / 1000);
      setSecondsLeft(diff > 0 ? diff : 0);
    };

    updateCounter();

    const interval = setInterval(updateCounter, 1000);

    return () => clearInterval(interval);
  }, [pagamentoLink, data?.bilhetes]);

  const isExpired = secondsLeft !== null && secondsLeft <= 0;
  const handleCompleted = useCallback((progress: CheckoutProgress) => {
    if (completionHandledRef.current) {
      return;
    }

    completionHandledRef.current = true;
    const target = progress.redirect_to || `/finalizada/${id}`;
    window.location.assign(target);
  }, [id]);

  const effectiveProgress = useVisualCheckoutProgress({
    pedidoId: id,
    method: "link",
    remoteProgress: checkoutProgress,
    localProgress,
    enabled: checkoutEffectsAllowed,
    onCompleted: handleCompleted,
  }) ?? mergeCheckoutProgress(checkoutProgress, localProgress);
  const formattedCounter =
    secondsLeft === null
      ? "--:--"
      : `${String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:${String(
          secondsLeft % 60
        ).padStart(2, "0")}`;

  useEffect(() => {
    if (!checkoutEffectsAllowed || !isExpired || !data?.pedido?.id || cancelOnExpireTriggered.current) {
      return;
    }

    if (pedido?.status === PedidoStatusCode.Cancelado) {
      cancelOnExpireTriggered.current = true;
      return;
    }

    cancelOnExpireTriggered.current = true;

    const cancelPedidoExpirado = async () => {
      setIsAutoCancelling(true);

      try {
        await api.delete(`/pedido/${pedido.id}/cancelar-por-expiracao`);

        await Swal.fire({
          icon: "info",
          title: "Reserva expirada",
          text: "O prazo da reserva terminou e o pedido foi cancelado automaticamente.",
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        window.location.assign("/");
      } catch (err: unknown) {
        const error = err as AxiosErrorResponse;
        const redirectTo = error.response?.data?.data?.redirect_to
          || error.response?.data?.redirect_to;
        const errorCode = error.response?.data?.data?.code
          || error.response?.data?.code;

        if (error.response?.status === 409) {
          if (['reserva_vigente', 'checkout_em_processamento'].includes(errorCode ?? '')) {
            cancelOnExpireTriggered.current = false;
            setSecondsLeft(1);
            return;
          }

          const pedidoFinalizado = errorCode === 'pedido_nao_disponivel'
            && redirectTo?.includes('/finalizada/');
          const possuiPagamentoOuConfirmacao = [
            'pagamento_registrado',
            'bilhete_confirmado',
          ].includes(errorCode ?? '');

          if (!pedidoFinalizado && !possuiPagamentoOuConfirmacao) {
            const message =
              error.response?.data?.data?.message
              || error.response?.data?.message
              || "Não foi possível confirmar o cancelamento automático do pedido.";

            await Swal.fire({
              icon: "warning",
              title: "Não foi possível cancelar o pedido",
              text: message,
              confirmButtonText: "Ver pedido",
              allowOutsideClick: false,
              allowEscapeKey: false,
            });

            window.location.replace(redirectTo || `/pedido/${pedido.id}`);
            return;
          }

          await Swal.fire({
            icon: "info",
            title: pedidoFinalizado ? "Pedido finalizado" : "Prazo da reserva encerrado",
            text: pedidoFinalizado
              ? "Este pedido já foi finalizado. Você será direcionado para a confirmação."
              : "O prazo da reserva terminou, mas o pedido possui pagamento ou confirmação vinculada. Verifique os detalhes do pedido antes de realizar qualquer nova ação.",
            confirmButtonText: "Ver pedido",
            allowOutsideClick: false,
            allowEscapeKey: false,
          });

          window.location.replace(
            pedidoFinalizado && redirectTo
              ? redirectTo
              : `/pedido/${pedido.id}`
          );

          return;
        }

        const message =
          error.response?.data?.data?.message ||
          error.response?.data?.message ||
          "A reserva expirou, mas não foi possível cancelar o pedido automaticamente.";

        await Swal.fire({
          icon: "warning",
          title: "Reserva expirada",
          text: message,
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        window.location.assign("/");
      } finally {
        setIsAutoCancelling(false);
      }
    };

    cancelPedidoExpirado();
  }, [isExpired, pedido?.id, pedido?.status, checkoutEffectsAllowed]);

  useEffect(() => {
    return () => {
      completionHandledRef.current = false;
    };
  }, []);

  if (isLoading || isRestoringPedido || (!hasValidPedido && isFetching) || isAutoCancelling) {
    return <Loading />;
  }

  if (isError || pedidoValidationFailed || !data || !pedido) {
    return (
      <Flex minH="60vh" alignItems="center" justifyContent="center">
        <Box bg="white" borderWidth={1} borderColor="gray.200" p={6} maxW="420px" textAlign="center">
          <Text fontSize="lg" fontWeight="bold" mb={2}>
            Não foi possível atualizar o status do pedido.
          </Text>
          <Text color="gray.600" mb={4}>
            Verifique sua conexão e tente novamente antes de continuar.
          </Text>
          <Button colorScheme="blue" onClick={() => void retryPedidoValidation()}>
            Tentar novamente
          </Button>
        </Box>
      </Flex>
    );
  }

  if (pedido.status === PedidoStatusCode.Finalizado) {
    return <Navigate to={`/finalizada/${pedido.id}`} replace />;
  }

  if (!pedidoDisponivelParaPagamento) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box
      display={"flex"}
      flexDir={"column"}
      justifyContent={"center"}
      width={"full"}
      alignItems={"center"}
    >
      <Flex
        maxWidth={"140rem"}
        width={"100%"}
        flexDir={"column"}
        justifyContent={"center"}
      >
        <Header />
        <Box px={"1rem"} bg={"white"} w={"full"} display={"flex"} flexDir={"column"} gap={"8"} pb={8}>
          <Limites />
          <Box bg={"#f4f4f4"} p={6}>
            <Heading size={"md"} mb={4}>
              Pagamento via Link WebPag
            </Heading>
            <Text fontSize={"lg"} mb={1}>
              Pedido: <strong>#{pedido.id}</strong>
            </Text>
            <Text fontSize={"2xl"} color={"blue.800"} fontWeight={"bold"} mb={6}>
              Total: {formatBRL(Number(pedido.valor_total))}
            </Text>
            <Text
              textAlign={"left"}
              color={"red.500"}
              fontWeight={"bold"}
              fontSize={"xl"}
              mb={4}
            >
              Expira em: {formattedCounter}
            </Text>

            <Text mb={2} fontWeight={"medium"}>
              Link de pagamento:
            </Text>
            <Text
              bg={"gray.100"}
              p={3}
              borderRadius={"md"}
              wordBreak={"break-all"}
              mb={4}
              fontSize={"sm"}
              color={isExpired ? "red.500" : "inherit"}
            >
              {isExpired
                ? "Pedido expirado, por favor gere um pedido novo."
                : pagamentoLink?.link ?? "Gerando link..."}
            </Text>
            <Box mb={6}>
              <CheckoutProgressPanel progress={effectiveProgress} fallbackStep="waiting_payment" method="link" />
            </Box>

            <Flex gap={3} flexWrap={"wrap"}>
              <Button
                isDisabled={!pagamentoLink?.link || isExpired}
                leftIcon={<Icon as={BiCopy} />}
                onClick={async () => {
                  const link = pagamentoLink?.link ?? "";
                  if (!link) return;

                  try {
                    if (navigator.clipboard && window.isSecureContext) {
                      await navigator.clipboard.writeText(link);
                    } else {
                      const textArea = document.createElement("textarea");
                      textArea.value = link;
                      textArea.style.position = "fixed";
                      textArea.style.left = "-999999px";
                      document.body.appendChild(textArea);
                      textArea.focus();
                      textArea.select();
                      document.execCommand("copy");
                      textArea.remove();
                    }

                    toast({
                      title: "Link copiado",
                      description: "Link de pagamento copiado para a área de transferência.",
                      status: "success",
                      duration: 2200,
                      isClosable: true,
                      position: "top-right",
                    });
                  } catch {
                    toast({
                      title: "Não foi possível copiar",
                      description: "Copie manualmente o link de pagamento.",
                      status: "warning",
                      duration: 2600,
                      isClosable: true,
                      position: "top-right",
                    });
                  }
                }}
              >
                Copiar Link de Pagamento
              </Button>
              <Button
                as={Link}
                href={pagamentoLink?.link ?? "#"}
                target={"_blank"}
                rel={"noopener noreferrer"}
                leftIcon={<Icon as={BiLinkExternal} />}
                colorScheme={"blue"}
                isDisabled={!pagamentoLink?.link || isExpired}
              >
                Abrir Link de Pagamento
              </Button>
              <Button
                variant={"outline"}
                onClick={() =>
                  (window.location.href = isExpired ? `/` : `/pagamento/${id}`)
                }
              >
                {isExpired ? "Ir para Passagens" : "Voltar"}
              </Button>
            </Flex>
          </Box>
        </Box>
        <Footer />
      </Flex>
    </Box>
  );
}
